import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Monitor, Activity, RefreshCw, Loader, ExternalLink, CheckCircle, XCircle, Server, AlertCircle, Loader2, Search, ChevronLeft, ChevronRight, ArrowUpDown, Clock, Settings, Play, Pause, Cpu, HardDrive, Wifi, Wrench, Package, Heart, ShieldOff, RotateCcw, AlertTriangle, Mail, Bell, X, Plus, Trash2, Shield, Timer, ChevronDown, ChevronUp, History } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

import NinjaEventHealth from '../components/NinjaEventHealth';
import RmmHealthDashboard from '../components/RmmHealthDashboard';
import { ApprovalQueue, RmmActionHistory } from '../components/RmmBulkActions';

/* ── Helpers ────────────────────────────────────────────────────── */
function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return `${m}m ${s}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function formatTimeUntil(isoStr) {
  if (!isoStr) return null;
  if (isoStr === 'imminent') return 'Imminent';
  try {
    const d = new Date(isoStr);
    const mins = Math.max(0, Math.floor((d.getTime() - Date.now()) / 60000));
    if (mins <= 0) return 'Imminent';
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  } catch { return '—'; }
}

const RUN_STATE_CONFIG = {
  DISABLED:  { label: 'Disabled',  color: 'bg-gray-100 text-gray-500 border-gray-200', dot: 'bg-gray-400' },
  IDLE:      { label: 'Idle',      color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  RUNNING:   { label: 'Running',   color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500 animate-pulse' },
  COMPLETED: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  COMPLETED_WITH_WARNINGS: { label: 'Warnings', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  FAILED:    { label: 'Failed',    color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
};

function RunStateBadge({ state }) {
  const cfg = RUN_STATE_CONFIG[state] || RUN_STATE_CONFIG.IDLE;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

/* ── Main Dashboard ─────────────────────────────────────────────── */
export default function NinjaRMMDashboard() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState({});
  const syncPollRef = useRef(null);

  // Pagination and sorting
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  // Auto-sync state (N1.1 separated)
  const [autoSyncConfig, setAutoSyncConfig] = useState({
    enabled: false, interval_minutes: 15,
    sections: ['processors', 'disks', 'networkInterfaces', 'services', 'software'],
    auto_exclude_after_failures: 5,
  });
  const [syncRunStatus, setSyncRunStatus] = useState({
    running: false, run_state: 'DISABLED', progress: null,
    last_run: null, last_run_status: null, assets_synced: 0, assets_failed: 0,
    next_scheduled_run: null,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const runPollRef = useRef(null);

  // Sync history
  const [syncHistory, setSyncHistory] = useState({ history: [], stats: {} });
  const [showHistory, setShowHistory] = useState(false);

  // Sync health
  const [syncHealth, setSyncHealth] = useState(null);

  // N4: Dashboard overview
  const [overview, setOverview] = useState(null);

  /* ── Data loading ────────────────────────────────────────────── */
  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/assets', { params: { has_ninjarmm: true, limit: 1000 } });
      setAssets(response.data);
    } catch {
      toast.error('Failed to load NinjaRMM data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSyncState = useCallback(async () => {
    try {
      const [configRes, statusRes, healthRes, overviewRes] = await Promise.all([
        api.get('/settings/ninjarmm-autosync'),
        api.get('/settings/ninjarmm-autosync/status'),
        api.get('/settings/ninjarmm-autosync/health').catch(() => ({ data: null })),
        api.get('/ninjarmm/dashboard-overview').catch(() => ({ data: null })),
      ]);
      setAutoSyncConfig(configRes.data);
      setSyncRunStatus(statusRes.data);
      if (healthRes.data) setSyncHealth(healthRes.data);
      if (overviewRes.data) setOverview(overviewRes.data);
    } catch { /* settings may not exist */ }
  }, []);

  const loadSyncHistory = useCallback(async () => {
    try {
      const res = await api.get('/settings/ninjarmm-autosync/history?limit=20');
      setSyncHistory(res.data);
    } catch { /* history not critical */ }
  }, []);

  useEffect(() => {
    loadAssets();
    loadSyncState();
    loadSyncHistory();
  }, [loadAssets, loadSyncState, loadSyncHistory]);

  /* ── N1.4: Controlled polling ─────────────────────────────────── */
  // Poll sync run status ONLY while running, stop at terminal state
  useEffect(() => {
    if (runPollRef.current) { clearInterval(runPollRef.current); runPollRef.current = null; }
    if (syncRunStatus.running) {
      runPollRef.current = setInterval(async () => {
        try {
          const res = await api.get('/settings/ninjarmm-autosync/status');
          setSyncRunStatus(res.data);
          if (!res.data.running) {
            clearInterval(runPollRef.current);
            runPollRef.current = null;
            loadSyncState();
            loadSyncHistory();
          }
        } catch {
          clearInterval(runPollRef.current);
          runPollRef.current = null;
        }
      }, 4000);
    }
    return () => { if (runPollRef.current) clearInterval(runPollRef.current); };
  }, [syncRunStatus.running, loadSyncState, loadSyncHistory]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
      if (runPollRef.current) clearInterval(runPollRef.current);
    };
  }, []);

  /* ── Filtering / Sorting ──────────────────────────────────────── */
  useEffect(() => {
    let filtered = [...assets];
    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.serial_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    filtered.sort((a, b) => {
      let aVal = a[sortBy] || '';
      let bVal = b[sortBy] || '';
      if (sortBy === 'status') { aVal = a.status === 'active' ? 1 : 0; bVal = b.status === 'active' ? 1 : 0; }
      if (typeof aVal === 'string') return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    setFilteredAssets(filtered);
    setCurrentPage(0);
  }, [assets, searchTerm, sortBy, sortOrder]);

  /* ── Handlers ─────────────────────────────────────────────────── */
  const handleAutoSyncToggle = async (enabled) => {
    setSavingConfig(true);
    try {
      const res = await api.put('/settings/ninjarmm-autosync', { ...autoSyncConfig, enabled });
      setAutoSyncConfig(res.data);
      loadSyncState();
      toast.success(enabled ? 'Auto-sync enabled' : 'Auto-sync disabled');
    } catch { toast.error('Failed to update'); }
    setSavingConfig(false);
  };

  const handleConfigSave = async () => {
    setSavingConfig(true);
    try {
      const res = await api.put('/settings/ninjarmm-autosync', autoSyncConfig);
      setAutoSyncConfig(res.data);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    setSavingConfig(false);
  };

  const handleRunNow = async () => {
    try {
      await api.post('/settings/ninjarmm-autosync/run-now');
      toast.success('System info sync started');
      setSyncRunStatus(prev => ({ ...prev, running: true, run_state: 'RUNNING' }));
    } catch { toast.error('Failed to start sync'); }
  };

  const handleAssetDiscovery = async () => {
    try {
      setSyncModalOpen(true);
      setSyncing(true);
      await api.post('/ninjarmm/discover-assets');
      syncPollRef.current = setInterval(async () => {
        try {
          const res = await api.get('/ninjarmm/sync-status');
          setSyncStatus(res.data);
          if (res.data.status === 'completed' || res.data.status === 'error') {
            clearInterval(syncPollRef.current);
            syncPollRef.current = null;
            if (res.data.status === 'completed') loadAssets();
          }
        } catch { /* poll error, will retry */ }
      }, 1000);
    } catch {
      toast.error('Failed to start asset discovery');
      setSyncModalOpen(false);
      setSyncing(false);
    }
  };

  const handleCloseSyncModal = () => {
    setSyncModalOpen(false);
    setSyncing(false);
    if (syncPollRef.current) { clearInterval(syncPollRef.current); syncPollRef.current = null; }
  };

  const toggleSection = (section) => {
    setAutoSyncConfig(prev => ({
      ...prev,
      sections: prev.sections?.includes(section)
        ? prev.sections.filter(s => s !== section)
        : [...(prev.sections || []), section]
    }));
  };

  const handleResetHealth = async (assetId) => {
    try {
      await api.post(`/assets/${assetId}/sync-health/reset`);
      toast.success('Sync health reset');
      loadSyncState();
    } catch { toast.error('Failed to reset'); }
  };

  /* ── Stats ────────────────────────────────────────────────────── */
  const stats = {
    total: assets.length,
    online: assets.filter(a => a.ninjarmm_data?.online || a.ninjarmm_data?.is_online || a.status === 'active').length,
    offline: assets.filter(a => (!a.ninjarmm_data?.online && !a.ninjarmm_data?.is_online && a.status !== 'active') || a.status === 'inactive').length,
    servers: assets.filter(a => a.asset_type === 'server').length,
    workstations: assets.filter(a => ['workstation', 'device', 'laptop'].includes(a.asset_type)).length,
  };

  const isRunning = syncRunStatus.running;
  const runState = syncRunStatus.run_state || (autoSyncConfig.enabled ? 'IDLE' : 'DISABLED');

  return (
    <div className="space-y-6 w-full p-6" data-testid="ninja-dashboard">
      {/* ── N1.6: Integration Health Header ──────────────────────── */}
      <section aria-label="Inventory reconciliation snapshot" className="space-y-2">
        <p className="text-xs leading-5 text-muted-foreground">Inventory reconciliation snapshot. Its historical counts and timestamps are separate from the scoped event and Remedy verification below.</p>
      <RmmHealthDashboard onNavigateMapping={() => navigate('/integrations')} />
      </section>

      {/* ── N4.1: Overview Summary Cards ────────────────────────── */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="ninja-overview">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/assets?has_ninjarmm=true')} data-testid="ov-devices">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-gray-500 uppercase">Devices</p><p className="text-xl font-bold">{overview.devices?.total || 0}</p><p className="text-[10px] text-gray-400">{overview.devices?.online || 0} online</p></CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md ${(overview.devices?.offline || 0) > 0 ? 'border-red-200' : ''}`} onClick={() => navigate('/assets?status=inactive&has_ninjarmm=true')} data-testid="ov-offline">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-red-600 uppercase">Offline</p><p className="text-xl font-bold text-red-600">{overview.devices?.offline || 0}</p></CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md ${(overview.tickets?.open_ninja || 0) > 0 ? 'border-amber-200' : ''}`} onClick={() => navigate('/tickets')} data-testid="ov-tickets">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-amber-600 uppercase">Ninja Tickets</p><p className="text-xl font-bold text-amber-600">{overview.tickets?.open_ninja || 0}</p></CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => navigate('/integrations/ninjaone/end-users')} data-testid="ov-endusers">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-blue-600 uppercase">End Users</p><p className="text-xl font-bold text-blue-600">{overview.end_users?.total || 0}</p><p className="text-[10px] text-gray-400">{overview.end_users?.mapped || 0} mapped</p></CardContent>
          </Card>
          <Card data-testid="ov-servers">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-gray-500 uppercase">Servers</p><p className="text-xl font-bold">{overview.devices?.servers || 0}</p></CardContent>
          </Card>
          <Card data-testid="ov-workstations">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-gray-500 uppercase">Workstations</p><p className="text-xl font-bold">{overview.devices?.workstations || 0}</p></CardContent>
          </Card>
          <Card className={`cursor-pointer hover:shadow-md ${(overview.organizations?.unmapped || 0) > 0 ? 'border-amber-200' : ''}`} onClick={() => navigate('/integrations')} data-testid="ov-orgs">
            <CardContent className="p-3"><p className="text-[11px] font-medium text-gray-500 uppercase">Orgs</p><p className="text-xl font-bold">{overview.organizations?.total || 0}</p>{(overview.organizations?.unmapped || 0) > 0 && <p className="text-[10px] text-amber-600">{overview.organizations.unmapped} unmapped</p>}</CardContent>
          </Card>
        </div>
      )}

      <NinjaEventHealth />

      {/* ── N1.9: Sync Center ────────────────────────────────────── */}
      <Card data-testid="sync-center">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-gray-700" />
                Sync Center
              </CardTitle>
              <p className="text-sm text-gray-500 mt-0.5">
                Asset and device polling from NinjaOne
              </p>
            </div>
            <div className="flex items-center gap-3">
              <RunStateBadge state={runState} />
              <div className="flex items-center gap-2">
                <Label htmlFor="autosync-toggle" className="text-sm">
                  Auto-Sync
                </Label>
                <Switch
                  id="autosync-toggle"
                  checked={autoSyncConfig.enabled}
                  onCheckedChange={handleAutoSyncToggle}
                  disabled={savingConfig}
                  data-testid="autosync-toggle"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ── Status display: clear state machine ── */}
          <div className={`rounded-lg p-4 ${isRunning ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`} data-testid="sync-status-panel">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Status</p>
                <div className="mt-1">
                  <RunStateBadge state={runState} />
                </div>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Last Success</p>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {syncRunStatus.last_run ? formatInCST(syncRunStatus.last_run, 'h:mm a') : 'Never'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Devices</p>
                <p className="text-sm font-medium text-gray-800 mt-1">{syncRunStatus.assets_synced || 0}</p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Errors</p>
                <p className={`text-sm font-medium mt-1 ${(syncRunStatus.assets_failed || 0) > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                  {syncRunStatus.assets_failed || 0}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Interval</p>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {autoSyncConfig.enabled ? `Every ${autoSyncConfig.interval_minutes}m` : 'Disabled'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Next Run</p>
                <p className="text-sm font-medium text-gray-800 mt-1">
                  {isRunning ? '—' : syncRunStatus.next_scheduled_run
                    ? formatTimeUntil(syncRunStatus.next_scheduled_run)
                    : autoSyncConfig.enabled ? 'Pending' : '—'}
                </p>
              </div>
            </div>

            {/* Running progress (ONLY shown when actually running) */}
            {isRunning && syncRunStatus.progress && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-200 text-sm text-blue-700" data-testid="sync-running-progress">
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                <span className="truncate">{syncRunStatus.progress}</span>
              </div>
            )}

            {/* Last status message (only when NOT running and there was a run) */}
            {!isRunning && syncRunStatus.last_run_status && (
              <p className="text-xs text-gray-500 mt-2">{syncRunStatus.last_run_status}</p>
            )}
          </div>

          {/* ── Actions row ── */}
          <div className="flex flex-wrap items-end gap-3">
            <Button
              size="sm"
              onClick={handleRunNow}
              disabled={isRunning}
              data-testid="sync-run-now-btn"
            >
              {isRunning
                ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Running...</>
                : <><Play className="h-4 w-4 mr-1.5" /> Sync Now</>}
            </Button>
            <Button size="sm" variant="outline" onClick={handleAssetDiscovery} disabled={syncing} data-testid="asset-discovery-btn">
              <Server className="h-4 w-4 mr-1.5" />
              Discover Assets
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate('/sync-dashboard')} data-testid="view-sync-dashboard-btn">
              <Activity className="h-4 w-4 mr-1.5" />
              Sync Dashboard
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowHistory(h => !h)} data-testid="toggle-history-btn">
              <History className="h-4 w-4 mr-1.5" />
              {showHistory ? 'Hide History' : 'Sync History'}
            </Button>
          </div>

          {/* ── Config: Interval + Sections ── */}
          {autoSyncConfig.enabled && (
            <div className="border rounded-lg p-3 space-y-3 bg-white">
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-48">
                  <Label className="text-xs text-gray-500">Sync Interval</Label>
                  <Select
                    value={String(autoSyncConfig.interval_minutes)}
                    onValueChange={(v) => setAutoSyncConfig(prev => ({ ...prev, interval_minutes: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="sync-interval-select" className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5,15,30,60,120,360,720,1440].map(v => (
                        <SelectItem key={v} value={String(v)}>
                          {v < 60 ? `${v} min` : v < 1440 ? `${v/60}h` : 'Daily'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="outline" onClick={handleConfigSave} disabled={savingConfig} data-testid="save-config-btn">
                  {savingConfig ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Save
                </Button>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1.5 block">Data Sections</Label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { key: 'processors', icon: Cpu, label: 'Processors' },
                    { key: 'disks', icon: HardDrive, label: 'Drives' },
                    { key: 'networkInterfaces', icon: Wifi, label: 'Network' },
                    { key: 'services', icon: Wrench, label: 'Services' },
                    { key: 'software', icon: Package, label: 'Software' },
                    { key: 'activities', icon: Activity, label: 'Activities' },
                    { key: 'alerts', icon: AlertCircle, label: 'Alerts' },
                  ].map(({ key, icon: Icon, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleSection(key)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        autoSyncConfig.sections?.includes(key)
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-gray-50 border-gray-200 text-gray-400 hover:bg-gray-100'
                      }`}
                      data-testid={`section-${key}`}
                    >
                      <Icon className="h-3 w-3" /> {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── N1.10: Sync History ── */}
          {showHistory && syncHistory.history.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="sync-history">
              <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b">
                <span className="text-xs font-medium text-gray-600">Recent Sync Runs</span>
                <span className="text-[11px] text-gray-400">
                  Avg: {formatDuration(syncHistory.stats?.avg_duration_seconds)} | Last {syncHistory.history.length} runs
                </span>
              </div>
              <div className="max-h-[220px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/50">
                      <TableHead className="text-[11px] py-1.5">Time</TableHead>
                      <TableHead className="text-[11px] py-1.5">Duration</TableHead>
                      <TableHead className="text-[11px] py-1.5">Synced</TableHead>
                      <TableHead className="text-[11px] py-1.5">Failed</TableHead>
                      <TableHead className="text-[11px] py-1.5">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncHistory.history.map((entry, i) => {
                      const hasFailed = (entry.assets_failed || 0) > 0;
                      const isSlow = entry.is_slow;
                      return (
                        <TableRow key={entry.id || i} className={hasFailed && entry.status?.startsWith('Error') ? 'bg-red-50/30' : isSlow ? 'bg-amber-50/30' : ''} data-testid={`history-row-${i}`}>
                          <TableCell className="text-xs py-1.5 text-gray-600">
                            {formatInCST(entry.started_at, 'h:mm a')}
                          </TableCell>
                          <TableCell className="py-1.5">
                            <span className={`text-xs font-medium ${isSlow ? 'text-amber-700' : 'text-gray-900'}`}>
                              {formatDuration(entry.duration_seconds)}
                            </span>
                            {isSlow && <AlertTriangle className="w-3 h-3 text-amber-500 inline ml-1" />}
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-emerald-700 font-medium">{entry.assets_synced}</TableCell>
                          <TableCell className="text-xs py-1.5">
                            {hasFailed ? <span className="text-red-600 font-medium">{entry.assets_failed}</span> : <span className="text-gray-400">0</span>}
                          </TableCell>
                          <TableCell className="text-xs py-1.5">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              entry.status?.startsWith('Error') ? 'bg-red-100 text-red-700' :
                              isSlow ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {entry.status?.startsWith('Error') ? 'Error' : isSlow ? 'Slow' : 'OK'}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          {showHistory && syncHistory.history.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">No sync history available yet</p>
          )}
        </CardContent>
      </Card>

      {/* ── Sync Health: Failing Devices ── */}
      {syncHealth && (syncHealth.failing > 0 || syncHealth.auto_excluded > 0 || syncHealth.unreachable_count > 0) && (
        <Card data-testid="sync-health-issues">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Sync Health Issues ({syncHealth.failing + syncHealth.auto_excluded + syncHealth.unreachable_count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-amber-50 rounded p-2 text-center">
                <p className="text-lg font-bold text-amber-600">{syncHealth.failing}</p>
                <p className="text-[11px] text-amber-700">Failing</p>
              </div>
              <div className="bg-red-50 rounded p-2 text-center">
                <p className="text-lg font-bold text-red-600">{syncHealth.auto_excluded}</p>
                <p className="text-[11px] text-red-700">Auto-Excluded</p>
              </div>
              <div className="bg-slate-50 rounded p-2 text-center">
                <p className="text-lg font-bold text-slate-600">{syncHealth.unreachable_count}</p>
                <p className="text-[11px] text-slate-700">Unreachable</p>
              </div>
            </div>
            {syncHealth.unreachable_devices?.length > 0 && (
              <div className="border rounded divide-y max-h-[200px] overflow-y-auto">
                {syncHealth.unreachable_devices.slice(0, 10).map(d => (
                  <div key={d.id} className="flex items-center justify-between p-2 text-sm hover:bg-gray-50">
                    <div className="min-w-0">
                      <span className="font-medium truncate block">{d.name}</span>
                      <span className="text-xs text-gray-500">{d.customer_name} | {d.consecutive_failures} failures</span>
                    </div>
                    <Button size="sm" variant="ghost" className="text-blue-600 flex-shrink-0" onClick={() => handleResetHealth(d.id)}>
                      <RotateCcw className="h-3 w-3 mr-1" /> Reset
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Approval Queue + Action History ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4" /> Pending Approvals</CardTitle>
          </CardHeader>
          <CardContent><ApprovalQueue /></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4" /> Action History</CardTitle>
          </CardHeader>
          <CardContent><RmmActionHistory /></CardContent>
        </Card>
      </div>

      {/* ── Statistics Cards ────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/assets?has_ninjarmm=true')} data-testid="stat-total">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-gray-500 uppercase">Total Devices</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200" data-testid="stat-online">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-emerald-600 uppercase flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Online</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.online}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200" data-testid="stat-offline">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-red-600 uppercase flex items-center gap-1"><XCircle className="w-3 h-3" /> Offline</p>
            <p className="text-2xl font-bold text-red-600">{stats.offline}</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-servers">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-blue-600 uppercase">Servers</p>
            <p className="text-2xl font-bold text-blue-600">{stats.servers}</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-workstations">
          <CardContent className="p-3">
            <p className="text-[11px] font-medium text-purple-600 uppercase">Workstations</p>
            <p className="text-2xl font-bold text-purple-600">{stats.workstations}</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Devices Table ────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
            <div>
              <CardTitle>Monitored Devices</CardTitle>
              <CardDescription>{filteredAssets.length} devices</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search devices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
                data-testid="device-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader className="w-6 h-6 animate-spin" /></div>
          ) : assets.length === 0 ? (
            <div className="text-center py-8">
              <Server className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No NinjaRMM devices found</p>
              <Button size="sm" className="mt-3" onClick={handleAssetDiscovery}>Discover Assets</Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {[
                      { key: 'name', label: 'Device Name' },
                      { key: 'asset_type', label: 'Type' },
                      { key: 'customer_name', label: 'Customer' },
                      { key: null, label: 'Device ID' },
                      { key: 'status', label: 'Status' },
                      { key: null, label: 'Actions' },
                    ].map(col => (
                      <TableHead
                        key={col.label}
                        className={col.key ? 'cursor-pointer hover:bg-gray-100' : ''}
                        onClick={col.key ? () => {
                          if (sortBy === col.key) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
                          else { setSortBy(col.key); setSortOrder('asc'); }
                        } : undefined}
                      >
                        <div className="flex items-center gap-1">
                          {col.label}
                          {col.key && <ArrowUpDown className="h-3 w-3" />}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map(asset => (
                    <TableRow
                      key={asset.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/assets/${asset.id}`)}
                      data-testid={`device-row-${asset.id}`}
                    >
                      <TableCell className="font-semibold">{asset.name}</TableCell>
                      <TableCell>
                        <Badge variant="custom" className="bg-blue-100 text-blue-800 text-xs">{asset.asset_type || 'Device'}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{asset.customer_name || 'No Customer'}</TableCell>
                      <TableCell className="font-mono text-xs">{asset.ninjarmm_device_id}</TableCell>
                      <TableCell>
                        <Badge variant="custom" className={
                          (asset.ninjarmm_data?.online || asset.ninjarmm_data?.is_online || asset.status === 'active')
                            ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }>
                          {(asset.ninjarmm_data?.online || asset.ninjarmm_data?.is_online || asset.status === 'active') ? 'Online' : 'Offline'}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="outline" onClick={() => {
                          window.open(`https://mathewsit.rmmservice.com/#/deviceDashboard/${asset.ninjarmm_device_id}/overview`, '_blank', 'noopener,noreferrer');
                        }}>
                          <ExternalLink className="h-3 w-3 mr-1" /> Dashboard
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {filteredAssets.length > 0 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rows:</span>
                    <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v)); setCurrentPage(0); }}>
                      <SelectTrigger className="w-16 h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{[10,25,50,100].map(v => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-600">
                      {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, filteredAssets.length)} of {filteredAssets.length}
                    </span>
                    <div className="flex gap-1">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCurrentPage(p => p + 1)} disabled={(currentPage + 1) * pageSize >= filteredAssets.length}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Asset Discovery Modal ────────────────────────────────── */}
      <Dialog open={syncModalOpen} onOpenChange={open => {
        if (!open && syncStatus.status !== 'completed' && syncStatus.status !== 'error') return;
        handleCloseSyncModal();
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Server className="h-5 w-5" /> Discovering NinjaRMM Assets</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex justify-center">
              {syncStatus.status === 'completed' ? <CheckCircle className="h-10 w-10 text-green-500" /> :
               syncStatus.status === 'error' ? <XCircle className="h-10 w-10 text-red-500" /> :
               <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />}
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm text-gray-600"><span>Progress</span><span>{syncStatus.progress || 0}%</span></div>
              <Progress value={syncStatus.progress || 0} className="h-2" />
            </div>
            <p className="text-sm text-gray-600 text-center">{syncStatus.message || 'Initializing...'}</p>
            <div className="flex justify-center gap-6 text-center">
              <div><p className="text-xl font-bold">{syncStatus.processed || 0}</p><p className="text-xs text-gray-500">Processed</p></div>
              <div><p className="text-xl font-bold text-green-600">{syncStatus.created || 0}</p><p className="text-xs text-gray-500">Created</p></div>
              <div><p className="text-xl font-bold text-blue-600">{syncStatus.updated || 0}</p><p className="text-xs text-gray-500">Updated</p></div>
            </div>
            {(syncStatus.status === 'completed' || syncStatus.status === 'error') && (
              <div className="flex justify-center"><Button onClick={handleCloseSyncModal}>Done</Button></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
